import { t } from "@lingui/core/macro";
import QRCode from "qrcode";
import { encodeIso88592 } from "../../../../shared/utils/tax-register";
import { PaymentType, PrinterConfig } from "./types";

export const getPaymentTypeLabel = (
  paymentType: PaymentType | "total",
): string => {
  switch (paymentType) {
    case "bank":
      return t`Bancno nakazilo`;
    case "online":
      return t`Spletno placilo`;
    case "paypal":
      return t`PayPal`;
    case "cash":
      return t`Gotovina`;
    case "card":
      return t`Kartica`;
    case "coupon":
      return t`Unovčen darilni bon`;
    case "crypto":
      return t`Kriptovaluta`;
    case "missing":
      return `${t`Manjkajoča plačila`} (TRR)`;
    case "other":
      return t`Ostalo`;
    case "total":
      return t`Skupaj`;
  }
};

export const buildHtmlQrCode = async (
  rawQrCodeData?: string,
  width?: string,
) => {
  // Generate QR Code as a Base64 Data URL (PNG image)
  const qrDataUrl = await QRCode.toDataURL(rawQrCodeData || "", {
    width: 10,
  });

  return `<img src="${qrDataUrl}" style="${width ? `width: ${width}` : "width: 50%"}; height: auto;" />`;
};

// The UPN QR standard mandates QR version 15 (77x77 modules), ECC level M
// and ISO 8859-2 encoded content; strict bank apps (NLB, Gorenjska banka)
// reject codes that deviate from it.
export const buildUpnHtmlQrCode = async (
  upnQrPayload: string,
  width?: string,
) => {
  const qrDataUrl = await QRCode.toDataURL(
    [{ data: encodeIso88592(upnQrPayload), mode: "byte" }],
    { version: 15, errorCorrectionLevel: "M", margin: 4 },
  );

  return `<img src="${qrDataUrl}" style="${width ? `width: ${width}` : "width: 50%"}; height: auto;" />`;
};

// A version 15 UPN QR symbol is 77 modules wide and ESC/POS font A is
// 12 dots per character, so the module size must shrink on narrow paper
// (e.g. 58 mm printers) for the symbol to fit the printable width.
export const getUpnQrModuleSize = (lineWidth: number) => {
  const printableDots = lineWidth * 12;

  return Math.min(6, Math.max(3, Math.floor(printableDots / 77)));
};

export const buildEscPosQrCode = (
  encodedData?: Uint8Array<ArrayBufferLike>,
  moduleSize: number = 6,
) => {
  if (!encodedData) return [];

  const qrDataLength = encodedData.length + 3;
  return Uint8Array.from([
    // Center alignment
    0x1b,
    0x61,
    0x01,

    // Set QR code model
    0x1d,
    0x28,
    0x6b,
    0x04,
    0x00,
    0x31,
    0x41,
    0x32,
    0x00, // Model 2 (standard)

    // Set QR code size (increase to make it bigger)
    0x1d,
    0x28,
    0x6b,
    0x03,
    0x00,
    0x31,
    0x43,
    moduleSize,

    // Set QR code error correction level (49 = M)
    0x1d,
    0x28,
    0x6b,
    0x03,
    0x00,
    0x31,
    0x45,
    0x31,

    // Store QR code data
    0x1d,
    0x28,
    0x6b,
    qrDataLength & 0xff, // pL
    (qrDataLength >> 8) & 0xff, // pH
    0x31,
    0x50,
    0x30, // cn, fn, m
    ...encodedData,

    // Print QR code
    0x1d,
    0x28,
    0x6b,
    0x03,
    0x00,
    0x31,
    0x51,
    0x30,

    // Reset alignment to left
    0x1b,
    0x61,
    0x00, // Left alignment
  ]);
};

// Helper function to get tax identification label based on provider and tax subject status
export const getTaxIdentificationLabel = (
  provider: string,
  isTaxSubject: boolean,
): string => {
  if (provider === "fina") {
    // Croatia uses unified OIB system
    return "OIB";
  } else {
    // Slovenia (FURS) and default: use different labels based on tax subject status
    return isTaxSubject ? "ID za DDV" : "Davcna stevilka";
  }
};

// Thermal receipts print without diacritics, matching getTaxIdentificationLabel.
export const getCompanyNumberLabel = (provider: string): string =>
  provider === "fina" ? "Maticni broj" : "Maticna stevilka";

type GetPaddingCommandsProps = {
  printerType: NonNullable<PrinterConfig>["printerType"];
  paddingTop: number;
  paddingBottom: number;
} & (
  | {
      printerType: Extract<
        NonNullable<PrinterConfig>["printerType"],
        "escpos_generic"
      >;
      encoder: TextEncoder;
    }
  | {
      printerType: Extract<
        NonNullable<PrinterConfig>["printerType"],
        "star_mi"
      >;
    }
);

// Function overloads for proper type discrimination
export function getPaddingCommands(data: {
  printerType: "escpos_generic";
  paddingTop: number;
  paddingBottom: number;
  encoder: TextEncoder;
}): {
  paddingTopCommands: Uint8Array<ArrayBuffer>;
  paddingBottomCommands: Uint8Array<ArrayBuffer>;
};

export function getPaddingCommands(data: {
  printerType: "star_mi";
  paddingTop: number;
  paddingBottom: number;
}): {
  paddingTopCommands: string;
  paddingBottomCommands: string;
};

// Implementation signature
export function getPaddingCommands(data: GetPaddingCommandsProps): {
  paddingTopCommands: Uint8Array<ArrayBuffer> | string;
  paddingBottomCommands: Uint8Array<ArrayBuffer> | string;
} {
  if (data.printerType === "escpos_generic") {
    return {
      paddingTopCommands: data.encoder.encode(`\n`.repeat(data.paddingTop)),
      paddingBottomCommands: data.encoder.encode(
        `\n`.repeat(data.paddingBottom),
      ),
    };
  }

  return {
    paddingTopCommands: `<div style="height: ${data.paddingTop * 2}px;"></div>`,
    paddingBottomCommands: `<div style="height: ${data.paddingBottom * 2}px;"></div>`,
  };
}
